import { forwardRef, memo, useEffect, useRef } from 'react';
import { Group, Rect, Circle, RegularPolygon, Star, Text, Line } from 'react-konva';
import Konva from 'konva';
import { Node } from '../../../../core/models/scene';

type BasicVariant =
  | 'rectangle'
  | 'circle'
  | 'triangle'
  | 'diamond'
  | 'hexagon'
  | 'pentagon'
  | 'star';

type TextAlign = 'left' | 'center' | 'right' | 'justify';
type VerticalAlign = 'top' | 'middle' | 'bottom';

interface BasicShapeNodeProps {
  node: Node;
  selected: boolean;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: any) => void;
  onDoubleClick?: (e: any) => void;
  onStartTextEdit?: (textNode: Konva.Text) => void;
  editing?: boolean;
  onTransformStart?: (e: any) => void;
  onTransformEnd?: (e: any) => void;
  onTransform?: (e: any) => void;
  canWrite?: boolean;
  labelPlacement?: 'inside' | 'below';
}

const resolveVariant = (node: Node): BasicVariant => {
  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const candidates = [
    attrs.variant,
    attrs.shapeType,
    node.type,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const normalized = candidate.toLowerCase() as BasicVariant;
      if (
        normalized === 'rectangle' ||
        normalized === 'circle' ||
        normalized === 'triangle' ||
        normalized === 'diamond' ||
        normalized === 'hexagon' ||
        normalized === 'pentagon' ||
        normalized === 'star'
      ) {
        return normalized;
      }
    }
  }
  return 'rectangle';
};

const LABEL_BELOW_GAP_PX = 5;

const BaseBasicShapeNode = forwardRef<Konva.Group, BasicShapeNodeProps>(({ 
  node,
  selected,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onDoubleClick,
  onStartTextEdit,
  editing = false,
  onTransformStart,
  onTransformEnd,
  onTransform,
  canWrite = true,
  labelPlacement: labelPlacementProp,
}, ref) => {
  const width = (node.width ?? node.w ?? 120) as number;
  const height = (node.height ?? node.h ?? 80) as number;
  const variant = resolveVariant(node);
  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const fromAttrs = attrs.labelPlacement;
  const labelPlacement: 'inside' | 'below' =
    labelPlacementProp ??
    (fromAttrs === 'below' || fromAttrs === 'inside' ? fromAttrs : 'inside');
  const isLabelBelow = labelPlacement === 'below';

  const groupRef = useRef<Konva.Group | null>(null);
  const shapeRef = useRef<Konva.Shape | null>(null);
  const boundsRef = useRef<Konva.Rect | null>(null);
  const textRef = useRef<Konva.Text | null>(null);
  const lastAutoHeightRef = useRef<number>(0);
  const baselineInnerHeightRef = useRef<{ key: string; value: number }>({ key: '', value: 0 });

  // Extract rotation from attrs.rotation (set by converter) or node.rotation (from Yjs)
  const rotation = typeof (node as any).rotation === 'number' 
    ? (node as any).rotation 
    : typeof attrs.rotation === 'number' 
      ? attrs.rotation 
      : 0;

  const fillColor =
    (node.fillColor as string) ||
    (attrs.fillColor as string) ||
    '#FFFFFF';
  const strokeColor =
    (node.strokeColor as string) ||
    (attrs.strokeColor as string) ||
    '#1E293B';
  const strokeWidth =
    typeof attrs.strokeWidth === 'number'
      ? (attrs.strokeWidth as number)
      : typeof node.strokeWidth === 'number'
        ? node.strokeWidth
        : 2;

  const textValue =
    typeof node.text === 'string'
      ? node.text
      : (attrs.label as string) || '';
  const renderedText = textValue.length > 0 ? textValue : ' ';
  const fontSize =
    typeof attrs.fontSize === 'number'
      ? (attrs.fontSize as number)
      : typeof node.fontSize === 'number'
        ? (node.fontSize as number)
        : 14;
  const fontFamily =
    (attrs.fontFamily as string) ||
    (node.fontFamily as string) ||
    'Inter, sans-serif';
  const textColor = (attrs.textColor as string) || '#0F172A';
  const hAlign = (attrs.textAlign as TextAlign) || 'center';
  const vAlign = (attrs.verticalAlign as VerticalAlign) || 'middle';
  const fontStyle = (() => {
    const fw = (attrs.fontWeight || '').toString().toLowerCase();
    const fs = (attrs.fontStyle || '').toString().toLowerCase();
    const isBold = fw === 'bold' || fw === '700';
    const isItalic = fs === 'italic';
    if (isBold && isItalic) return 'bold italic';
    if (isBold) return 'bold';
    if (isItalic) return 'italic';
    return 'normal';
  })();
  const textDecoration = (attrs.textDecoration as string) || 'none';

  const dash = Array.isArray(attrs.dash) ? attrs.dash : [];
  const dashEnabled = !!attrs.dashEnabled && dash.length > 0;

  const fireDrag = (handler?: (pos: { x: number; y: number }) => void) => (e: any) => {
    if (!handler) return;
    const pos = e.target.position();
    handler({ x: pos.x, y: pos.y });
  };

  const applyDimensions = (nextWidth: number, nextHeight: number) => {
    const group = groupRef.current;
    const shape = shapeRef.current as any;
    if (group) {
      group.width(nextWidth);
      group.height(nextHeight);
    }
    if (shape) {
      switch (variant) {
        case 'rectangle': {
          (shape as Konva.Rect).width(nextWidth);
          (shape as Konva.Rect).height(nextHeight);
          break;
        }
        case 'circle': {
          const circle = shape as Konva.Circle;
          const radius = Math.max(4, Math.min(nextWidth, nextHeight) / 2);
          circle.radius(radius);
          circle.position({ x: nextWidth / 2, y: nextHeight / 2 });
          break;
        }
        case 'triangle': {
          const line = shape as Konva.Line;
          line.points([
            nextWidth / 2, 0,
            nextWidth, nextHeight,
            0, nextHeight,
          ]);
          break;
        }
        case 'hexagon':
        case 'pentagon': {
          const polygon = shape as Konva.RegularPolygon;
          const radius = Math.max(4, Math.min(nextWidth, nextHeight) / 2);
          polygon.radius(radius);
          polygon.position({ x: nextWidth / 2, y: nextHeight / 2 });
          break;
        }
        case 'diamond': {
          const line = shape as Konva.Line;
          line.points([
            nextWidth / 2, 0,
            nextWidth, nextHeight / 2,
            nextWidth / 2, nextHeight,
            0, nextHeight / 2,
          ]);
          break;
        }
        case 'star': {
          const star = shape as Konva.Star;
          const outer = Math.max(4, Math.min(nextWidth, nextHeight) / 2);
          star.outerRadius(outer);
          star.innerRadius(outer * 0.5);
          star.position({ x: nextWidth / 2, y: nextHeight / 2 });
          break;
        }
        default:
          break;
      }
    }
    if (textRef.current) {
      if (isLabelBelow) {
        textRef.current.width(Math.max(0, nextWidth));
        textRef.current.y(nextHeight + LABEL_BELOW_GAP_PX);
      } else {
        const innerX = 12;
        const innerY = 12;
        const innerWidth = Math.max(0, nextWidth - 24);
        const innerHeight = Math.max(0, nextHeight - 24);
        if (boundsRef.current) {
          boundsRef.current.x(innerX);
          boundsRef.current.y(innerY);
          boundsRef.current.width(innerWidth);
          boundsRef.current.height(innerHeight);
        }
        textRef.current.x(innerX);
        textRef.current.y(innerY);
        textRef.current.width(innerWidth);
        textRef.current.height(innerHeight);
      }
    }
  };

  useEffect(() => {
    applyDimensions(width, height);
  }, [width, height, variant, isLabelBelow, renderedText, fontSize, fontFamily, fontStyle, textDecoration, hAlign, vAlign, editing]);

  useEffect(() => {
    if (isLabelBelow) return;
    const bounds = boundsRef.current as any;
    if (!bounds) return;

    const measureBaselineInnerHeight = (innerWidth: number): number => {
      const key = `${innerWidth}:${fontSize}:${fontFamily}:${fontStyle}:${textDecoration}:${hAlign}:${vAlign}`;
      if (baselineInnerHeightRef.current.key === key && baselineInnerHeightRef.current.value > 0) {
        return baselineInnerHeightRef.current.value;
      }

      try {
        const probe = new Konva.Text({
          text: 'A',
          fontSize,
          fontFamily,
          fontStyle,
          textDecoration,
          width: innerWidth,
          align: hAlign,
          verticalAlign: vAlign,
        } as any);
        const rect = probe.getClientRect({ skipShadow: true });
        const raw = rect?.height ?? 0;
        const baseline = Math.max(18, typeof raw === 'number' && raw > 0 ? raw : fontSize * 1.2);
        baselineInnerHeightRef.current = { key, value: baseline };
        return baseline;
      } catch {
        const baseline = Math.max(18, fontSize * 1.2);
        baselineInnerHeightRef.current = { key, value: baseline };
        return baseline;
      }
    };

    const onBoundsHeightChange = () => {
      try {
        const innerHeight = typeof bounds.height === 'function' ? (bounds.height() as number) : 0;
        if (!Number.isFinite(innerHeight) || innerHeight <= 0) return;

        const group = groupRef.current as any;
        if (!group) return;
        const currentHeight = (group?.height?.() as number) || height;
        const currentWidth = (group?.width?.() as number) || width;
        const innerWidth = currentWidth - 24;
        if (!Number.isFinite(innerWidth) || innerWidth <= 0) return;

        const baseline = measureBaselineInnerHeight(innerWidth);
        if (innerHeight <= baseline + 2) return;

        const desiredHeight = Math.max(20, currentHeight, innerHeight + 24);
        if (desiredHeight <= currentHeight + 0.5) return;
        if (desiredHeight <= (lastAutoHeightRef.current || 0) + 0.5) return;

        lastAutoHeightRef.current = desiredHeight;
        applyDimensions(currentWidth, desiredHeight);
        try { group?.getLayer?.()?.batchDraw?.(); } catch {}
        if (selected) {
          try {
            const stage = group?.getStage?.();
            const transformers = stage?.find?.('Transformer') || [];
            for (const transformer of transformers) {
              try { transformer.forceUpdate?.(); } catch {}
              try { transformer.getLayer?.()?.batchDraw?.(); } catch {}
            }
          } catch {}
        }
      } catch {}
    };

    bounds.on('heightChange', onBoundsHeightChange);
    return () => {
      try { bounds.off('heightChange', onBoundsHeightChange); } catch {}
    };
  }, [width, height, selected, fontSize, fontFamily, fontStyle, textDecoration, hAlign, vAlign, isLabelBelow]);

  const handleTransform = (e: any) => {
    const target = e.target as any;
    const scaleX = target.scaleX?.() ?? 1;
    const scaleY = target.scaleY?.() ?? 1;
    const currentWidth = (groupRef.current?.width?.() as number) || width;
    const currentHeight = (groupRef.current?.height?.() as number) || height;
    const nextWidth = Math.max(20, currentWidth * scaleX);
    const nextHeight = Math.max(20, currentHeight * scaleY);
    applyDimensions(nextWidth, nextHeight);
    try { target.scaleX(1); target.scaleY(1); } catch {}
    onTransform?.(e);
  };

  const handleTransformEnd = (e: any) => {
    // Let the canvas-level handler read the live scale before it resets the node;
    // avoid resetting dimensions here so the resize persists via the command path.
    onTransformEnd?.(e);
  };

  const handleDoubleClick = (e: any) => {
    e.cancelBubble = true;
    onDoubleClick?.(e);
    if (!canWrite || !onStartTextEdit) return;

    if (textRef.current) {
      onStartTextEdit(textRef.current);
      return;
    }

    const group = groupRef.current as any;
    const textNodes = group?.find?.('Text') || [];
    if (textNodes.length > 0) {
      onStartTextEdit(textNodes[0] as Konva.Text);
    }
  };

  const renderVariant = () => {
    const commonShapeProps = {
      fill: fillColor,
      stroke: strokeColor,
      strokeWidth,
      strokeEnabled: strokeWidth > 0,
      perfectDrawEnabled: false,
      dash: dashEnabled ? dash : undefined,
      dashEnabled: dashEnabled,
    };

    switch (variant) {
      case 'rectangle':
        return (
          <Rect
            ref={shapeRef as any}
            {...commonShapeProps}
            x={0}
            y={0}
            width={width}
            height={height}
            cornerRadius={4}
          />
        );
      case 'circle': {
        const radius = Math.min(width, height) / 2;
        return (
          <Circle
            ref={shapeRef as any}
            {...commonShapeProps}
            x={width / 2}
            y={height / 2}
            radius={radius}
          />
        );
      }
      case 'triangle':
        return (
          <Line
            ref={shapeRef as any}
            {...commonShapeProps}
            x={0}
            y={0}
            points={[
              width / 2, 0,
              width, height,
              0, height,
            ]}
            closed
          />
        );
      case 'diamond':
        return (
          <Line
            ref={shapeRef as any}
            {...commonShapeProps}
            x={0}
            y={0}
            points={[
              width / 2, 0,
              width, height / 2,
              width / 2, height,
              0, height / 2,
            ]}
            closed
          />
        );
      case 'hexagon':
        return (
          <RegularPolygon
            ref={shapeRef as any}
            {...commonShapeProps}
            x={width / 2}
            y={height / 2}
            sides={6}
            radius={Math.min(width, height) / 2}
          />
        );
      case 'pentagon':
        return (
          <RegularPolygon
            ref={shapeRef as any}
            {...commonShapeProps}
            x={width / 2}
            y={height / 2}
            sides={5}
            radius={Math.min(width, height) / 2}
          />
        );
      case 'star': {
        const outer = Math.min(width, height) / 2;
        return (
          <Star
            ref={shapeRef as any}
            {...commonShapeProps}
            x={width / 2}
            y={height / 2}
            numPoints={5}
            outerRadius={outer}
            innerRadius={outer * 0.5}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <Group
      ref={(nodeEl) => {
        groupRef.current = nodeEl as Konva.Group | null;
        if (typeof ref === 'function') ref(nodeEl as any);
        else if (ref && typeof ref === 'object') (ref as any).current = nodeEl;
      }}
      id={node.id}
      name="shape"
      x={node.x}
      y={node.y}
      width={width}
      height={height}
      rotation={rotation}
      draggable={canWrite && !editing}
      onDragStart={fireDrag(onDragStart)}
      onDragMove={fireDrag(onDragMove)}
      onDragEnd={fireDrag(onDragEnd)}
      onClick={onClick}
      onDblClick={canWrite ? handleDoubleClick : undefined}
      onDblTap={canWrite ? handleDoubleClick : undefined}
      onTransformStart={onTransformStart}
      onTransformEnd={handleTransformEnd}
      onTransform={handleTransform}
    >
      {renderVariant()}
      {isLabelBelow ? (
        <Text
          key="label-below"
          ref={textRef as any}
          x={0}
          y={height + LABEL_BELOW_GAP_PX}
          width={Math.max(0, width)}
          text={renderedText}
          align="center"
          verticalAlign="top"
          wrap="word"
          listening={false}
          visible={!editing}
          fontSize={fontSize}
          fontFamily={fontFamily}
          fontStyle={fontStyle}
          textDecoration={textDecoration}
          fill={textValue ? textColor : 'transparent'}
        />
      ) : (
        <>
          <Rect
            ref={boundsRef as any}
            name="text-bounds"
            x={12}
            y={12}
            width={Math.max(0, width - 24)}
            height={fontSize}
            listening={false}
            fill="rgba(0,0,0,0)"
            strokeEnabled={false}
            perfectDrawEnabled={false}
            disableOverlayLiveResize={true}
          />
          <Text
            ref={textRef as any}
            x={12}
            y={12}
            width={Math.max(0, width - 24)}
            height={Math.max(0, height - 24)}
            text={renderedText}
            align={hAlign}
            verticalAlign={vAlign}
            listening={false}
            visible={!editing}
            fontSize={fontSize}
            fontFamily={fontFamily}
            fontStyle={fontStyle}
            textDecoration={textDecoration}
            fill={textValue ? textColor : 'transparent'}
          />
        </>
      )}
    </Group>
  );
});
BaseBasicShapeNode.displayName = 'BasicShapeNode';

export const BasicShapeNode = memo(BaseBasicShapeNode);

export default BasicShapeNode;
